import type { UserRole } from './types'
import { visibleMasterTabs } from './masters-access'

/**
 * WHO SEES WHICH SCREEN.
 *
 * This used to live inside AppShell.tsx, mixed in with icons and Tailwind
 * classes. It is policy, not layout, and burying it in a component is how the
 * following happened twice:
 *
 *   - Accounts was given the four register exports on '/reports', and could not
 *     reach the screen they live on
 *   - Accounts was given read access to the vendor master, and could not reach
 *     the screen it lives on
 *
 * Both times the permission was widened in one place and the sidebar was
 * governed by another. Both times the screen behind the link worked perfectly.
 *
 * It is a plain module with no React in it so the unit tests can import it
 * directly. Importing AppShell.tsx into the test runner pulled in React,
 * lucide, react-router and the '@/' path alias, and fell over on the alias -
 * which is a good sign that the test was reaching into the wrong place.
 */

/**
 * The base rule: which roles may open each route.
 *
 * A route absent from this map is open to every signed-in user - the goods
 * receipt and issue screens, which every store-man needs.
 */
export const ROUTE_ROLES: Partial<Record<string, UserRole[]>> = {
  '/indents': ['site_construction_manager', 'storeman', 'store_executive',
               'stores_head', 'admin', 'cxo'],
  '/ists': ['stores_head', 'admin', 'cxo', 'storeman', 'store_executive'],
  '/payables': ['procurement_manager', 'procurement_head', 'stores_head', 'admin', 'cxo'],
  '/vendor-invoices': ['accounts_spoc', 'stores_head', 'procurement_head',
                       'project_head', 'cmd', 'cxo', 'admin'],
  // The three Procurement screens a Project Manager was given on 11-09-2026.
  // He answers for his sites, so he is entitled to know what is still owed
  // against them, what his store-man spent in cash, and whether the paperwork
  // has reached Accounts. Read-only on all three.
  //
  // Adding him HERE is necessary and not sufficient: he also appears in
  // RESTRICTED_MENUS below, and that allow-list is consulted first. Both, or
  // neither works. See the warning on RESTRICTED_MENUS.
  //
  // What he actually SEES is decided in the database, not here - migration
  // 20261024000001 scopes vendor_payables and vendor_payments to the sites
  // assigned to him and makes v_payable_settlement honour the rule. This map
  // opens a door; it does not decide what is behind it.
  '/liabilities': ['cmd', 'cxo', 'project_head', 'accounts_spoc', 'stores_head',
                   'procurement_head', 'admin', 'project_manager'],
  // Board mandate 23-09-2026. The Accounts ledger's vendor balances brought
  // forward. NOT the Project Manager: a vendor-wide legacy balance cannot be
  // scoped to his sites (migration 0179). cmd and project_head see it through
  // SEES_EVERY_SCREEN, and Accounts through RESTRICTED_MENUS below.
  '/opening-liabilities': ['accounts_spoc', 'procurement_manager', 'procurement_head',
                           'project_head', 'stores_head', 'cmd', 'cxo', 'admin'],
  '/petty-cash': ['accounts_spoc', 'procurement_head', 'procurement_manager',
                  'project_head', 'stores_head', 'admin', 'project_manager'],
  '/grn-register': ['accounts_spoc', 'stores_head', 'procurement_head',
                    'procurement_manager', 'project_head', 'cmd', 'cxo', 'admin',
                    'project_manager'],
  '/credit-notes': ['procurement_manager', 'procurement_head', 'stores_head', 'admin', 'cxo'],
  // A Project Manager joined 11-09-2026: read-only, and only payments traceable
  // to his own sites (migration 20261024000001 decides that, not this list).
  '/payments': ['procurement_manager', 'procurement_head', 'stores_head', 'admin', 'cxo',
                'project_manager'],
  '/rates': ['procurement_head', 'procurement_manager', 'stores_head', 'admin'],
  '/assets': ['stores_head', 'admin', 'cxo', 'procurement_head'],
  '/late-entries': ['stores_head', 'procurement_head', 'admin', 'cxo'],
  // '/masters' is deliberately ABSENT from this map. Who may open Master Data
  // is decided by the board's access matrix - a role with at least one visible
  // tab gets the link - and duplicating that as a role list here is exactly
  // the two-lists-one-truth problem that hid the screen from Accounts.
  // See canSeeRoute() below.
  '/periods': ['accounts_spoc', 'admin', 'procurement_head', 'project_head',
               'cmd', 'cxo', 'stores_head'],
  '/users': ['admin'],
  // System Settings, 29-09-2026 (0184). The administrator alone - and listed
  // in NEVER_SHOWN below too, so the CMD and Project Head, who otherwise see
  // every screen, do not get a page whose only purpose is to change things.
  '/system-settings': ['admin'],
  // Requisition housekeeping (0188): Procurement acts on it. The CMD and the
  // Project Head reach it through SEES_EVERY_SCREEN and see it read-only.
  '/pr-housekeeping': ['procurement_manager', 'procurement_head', 'admin'],
}

/**
 * An ALLOW-LIST, and it OVERRIDES ROUTE_ROLES completely.
 *
 * THIS IS THE TRAP. Widening ROUTE_ROLES for a role that appears here changes
 * nothing at all - the allow-list is consulted first and the base rule is never
 * reached. If you give one of these roles a new capability, the route has to be
 * added HERE as well.
 */
export const RESTRICTED_MENUS: Partial<Record<UserRole, string[]>> = {
  // Money, and the orders she counter-signs. She is the third signature on
  // every purchase order, so she must be able to open one; Last Purchase Rates
  // is how she judges whether a price is reasonable before she does.
  //
  // Read-only on all of it EXCEPT Payments Made, where she has the New payment
  // button - advance against an order, or settlement of an invoice.
  //
  // '/reports' and '/masters' are both here because of the rule above: she was
  // permitted to download files, and later to read the vendor master, from
  // screens the sidebar would not draw for her.
  accounts_spoc: ['/', '/pos', '/payables', '/vendor-invoices', '/grn-register',
                  '/liabilities', '/petty-cash', '/credit-notes', '/payments',
                  '/rates', '/reports', '/periods', '/sync',
                  // 23-09-2026: she loads the opening balances and enters the
                  // payments against them (migration 0179).
                  '/opening-liabilities'],

  // Every screen the Head of Stores works in, for the sites assigned to him -
  // goods receipts, issues and returns are all view-only, because he holds no
  // capability that would put a button on them.
  //
  // Widened 11-09-2026 with four Procurement screens: what is still owed
  // against his sites, what was bought with site cash, what has been paid,
  // and whether Accounts has the paperwork. Read-only, and scoped in the database to the sites on
  // which he is named Project Manager - never head office, never a colleague's
  // project. '/liabilities' in particular is a board screen for everybody else
  // and a portfolio screen for him: the totals are computed from the rows row
  // level security lets him read, so "company total" means "his total".
  project_manager: ['/', '/indents', '/prs', '/pos', '/grns', '/mins', '/mrns',
                    '/inventory', '/reports', '/sync',
                    '/liabilities', '/petty-cash', '/grn-register', '/payments'],
}

/**
 * Roles that may open every screen.
 *
 * The CMD signs for the company and the Project Head answers for every site, so
 * neither should have to ask what is on a screen they are accountable for.
 * Neither gains a single write: every create and approve button is gated on the
 * CAN matrix, and neither role appears in any of them beyond its own signature.
 */
export const SEES_EVERY_SCREEN: UserRole[] = ['cmd', 'project_head']

/**
 * ...with one exception. Creating and disabling user accounts is
 * administration, not oversight, and it stays with the system administrator.
 */
export const NEVER_SHOWN: string[] = ['/users', '/system-settings']

/**
 * The single answer, used by the sidebar and by the tests.
 *
 * Order matters and is not obvious, which is the whole reason this is one
 * function in one place rather than four lists read in four different ways.
 */
export function canSeeRoute(role: UserRole, to: string): boolean {
  if (NEVER_SHOWN.includes(to) && role !== 'admin') return false
  // Master Data answers for itself, from the board matrix, BEFORE the
  // allow-list is consulted - otherwise every role given a new master would
  // also have to be added to RESTRICTED_MENUS, which is the trap this file
  // exists to close.
  if (to === '/masters') return visibleMasterTabs(role).length > 0
  const allow = RESTRICTED_MENUS[role]
  if (allow) return allow.includes(to)
  if (SEES_EVERY_SCREEN.includes(role)) return true
  const roles = ROUTE_ROLES[to]
  return roles ? roles.includes(role) : true
}
